import { useNavigate } from 'react-router-dom';
import React from 'react';
import * as Icon from 'react-feather';
import { AccountIcons } from '../../components/general/AccountIcons';

function formatDate(gmtDate) {
  if (!gmtDate) return null;
  try {
    const p = gmtDate.split(/[- :]/);
    const d = new Date(Date.UTC(p[0], p[1] - 1, p[2], p[3], p[4], p[5]));
    return d.toLocaleString(undefined, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hour12: true, hour: 'numeric', minute: 'numeric',
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return 'Invalid date'; }
}

function PostedPost({ myItem, viewMode = 'card' }) {
  const navigate = useNavigate();
  const isFailed = myItem.status === 'failed';
  const isDraft   = !isFailed && !myItem.posted_date;
  const formattedDate = formatDate(myItem.posted_date);
  const accentColor = isFailed ? '#ef4444' : isDraft ? '#f59e0b' : '#16a34a';

  if (viewMode === 'list') {
    return (
      <div className="col-12" style={{ marginBottom: 8 }}>
        <div style={{
          background: '#fff',
          border: '1px solid #d1d9e0',
          borderLeft: `4px solid ${accentColor}`,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '11px 16px',
        }}>

          {/* Left: accounts + date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0, width: 190 }}>
            <AccountIcons accounts={myItem.accounts} />
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: isFailed ? '#dc2626' : isDraft ? '#b45309' : '#15803d' }}>
              {isFailed ? `Failed · ${formattedDate || 'Unknown date'}` : isDraft ? 'Draft' : formattedDate}
            </span>
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontSize: '0.875rem', color: '#334155', lineHeight: 1.5,
              margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {myItem.content
                ? myItem.content
                : myItem.videoTitle || <span style={{ color: '#94a3b8' }}>No content</span>}
            </p>
          </div>

          {/* Images preview */}
          {myItem.images?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {myItem.images.slice(0, 2).map((img, i) => (
                <img
                  key={i}
                  src={img.url || img}
                  alt=""
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #d1d9e0' }}
                  onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          )}

          {/* Button */}
          <button
            className="btn btn-sm"
            style={{ fontSize: '0.78rem', border: '1px solid #d1d9e0', borderRadius: 7, color: '#475569', background: '#fff', flexShrink: 0 }}
            onClick={() => navigate('/single-post/' + (myItem.pid || myItem.id))}
          >
            <Icon.Eye size={12} className="me-1" />
            {isDraft ? 'Edit draft' : 'View post'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 col-md-6 col-xl-3" style={{ marginBottom: 20 }}>
      <div style={{
        background: '#fff',
        border: '1px solid #b0bec5',
        borderTop: `3px solid ${accentColor}`,
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>

        {/* Top row: accounts + status badge */}
        <div style={{ padding: '14px 16px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <AccountIcons accounts={myItem.accounts} />
          {isFailed ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#fef2f2', color: '#dc2626',
              fontSize: '0.72rem', fontWeight: 600,
              padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <Icon.AlertCircle size={11} /> Failed · {formattedDate || 'Unknown date'}
            </span>
          ) : isDraft ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#fefce8', color: '#92400e',
              fontSize: '0.72rem', fontWeight: 600,
              padding: '3px 8px', borderRadius: 20, flexShrink: 0,
            }}>
              <Icon.FileText size={11} /> Draft
            </span>
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#f0fdf4', color: '#15803d',
              fontSize: '0.72rem', fontWeight: 600,
              padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <Icon.CheckCircle size={11} /> {formattedDate}
            </span>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '12px 16px', flexGrow: 1 }}>
          <p style={{ fontSize: '0.875rem', color: '#334155', lineHeight: 1.65, margin: 0 }}>
            {myItem.content
              ? <>{myItem.content.substring(0, 180)}{myItem.content.length > 180 && <span style={{ color: '#94a3b8' }}>…</span>}</>
              : myItem.videoTitle || <span style={{ color: '#94a3b8' }}>No content</span>}
          </p>

          {myItem.images?.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {myItem.images.slice(0, 3).map((img, i) => (
                <a key={i} href={img.url || img} target="_blank" rel="noopener noreferrer">
                  <img
                    src={img.url || img}
                    alt=""
                    style={{ width: 68, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid #d1d9e0' }}
                    onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #e8edf2' }}>
          <button
            className="btn btn-sm sp-btn-ghost"
            style={{ fontSize: '0.78rem', border: '1px solid #d1d9e0', borderRadius: 7, color: '#475569', background: '#fff' }}
            onClick={() => navigate('/single-post/' + (myItem.pid || myItem.id))}
          >
            <Icon.Eye size={12} className="me-1" />
            {isFailed ? 'View post' : isDraft ? 'Edit draft' : 'View post'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PostedPost;
